import { resolveInvivoMaterialPlan } from "../rendering/materials/resolve_invivo_material_plan";
import { buildSceneRenderPlan } from "../rendering/build_scene_render_plan";
import type {
  InvivoBackgroundDescriptor,
  InvivoLightingSettings,
  InvivoMaterialPlan,
  InvivoRenderSettings,
  InvivoSceneDefinition,
  InvivoSceneManifestEntry,
  NormalizedScenePackage,
} from "../types/invivo_scene";
import { resolveLegacyAssetRoots } from "./asset_roots";
import { validateLegacyBackgroundPreset, validateLegacyMaterialPlan } from "./scene_validation";
import { buildLegacyBackgroundDescriptor } from "./background_catalog";
import { buildLegacyLightingSettings, buildLegacyRenderSettings } from "./render_settings_catalog";

export interface NormalizedLegacyScenePackage extends NormalizedScenePackage {
  manifest: InvivoSceneManifestEntry;
  definition: InvivoSceneDefinition;
  assetBaseUrl: string;
  sharedAssetBaseUrl: string;
  textureBaseUrl: string;
  modelUrl: string;
  availableTextureFiles: string[];
  backgroundValue: string;
  background: InvivoBackgroundDescriptor;
  renderSettings: InvivoRenderSettings;
  lightingSettings: InvivoLightingSettings;
  materialPlans: Map<string, InvivoMaterialPlan>;
}

export function normalizeLegacyScene({
  manifest,
  appUrl,
}: {
  manifest: InvivoSceneManifestEntry;
  appUrl: string;
}): NormalizedLegacyScenePackage {
  const { assetBaseUrl, textureBaseUrl, sharedAssetBaseUrl } = resolveLegacyAssetRoots({ manifest, appUrl });
  const backgroundValue =
    typeof manifest.definition.background === "string" && manifest.definition.background.length > 0
      ? manifest.definition.background
      : "or";

  validateLegacyBackgroundPreset(backgroundValue);
  const background = buildLegacyBackgroundDescriptor({
    backgroundValue,
    sharedAssetBaseUrl,
  });

  const materialNames = new Set<string>();
  const renderPlan = buildSceneRenderPlan(manifest.definition);

  Object.values(renderPlan.meshMaterialAssignments).forEach((materialName) => {
    if (typeof materialName === "string" && materialName.length > 0) {
      materialNames.add(materialName);
    }
  });

  Object.keys(manifest.definition.materials || {}).forEach((name) => materialNames.add(name));

  const materialPlans = new Map<string, InvivoMaterialPlan>();
  materialNames.forEach((materialName) => {
    const materialPlan = resolveInvivoMaterialPlan({
      definition: manifest.definition,
      assetBaseUrl,
      textureBaseUrl,
      sharedAssetBaseUrl,
      availableTextureFiles: manifest.availableTextureFiles,
      materialName,
    });
    validateLegacyMaterialPlan(materialPlan);
    materialPlans.set(materialName, materialPlan);
  });

  return {
    manifest,
    definition: manifest.definition,
    assetBaseUrl,
    textureBaseUrl,
    sharedAssetBaseUrl,
    modelUrl: new URL(`${manifest.modelFile}`, assetBaseUrl).toString(),
    availableTextureFiles: manifest.availableTextureFiles,
    backgroundValue,
    background,
    renderSettings: buildLegacyRenderSettings(),
    lightingSettings: buildLegacyLightingSettings(),
    materialPlans,
  };
}
